import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteDefaultQueryStateFromTable } from 'components/table/utlil';
import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { Institution } from 'modules/institutions/core/type';

import type { Trainer } from '../type';

import { trimValue } from 'helpers';
import { useNumber, useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { debounce, isEmpty, isUndefined } from 'lodash';

import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useFullTimeController = () => {
  const userProfile = useAuthStore(userProfileSelector);
  const roleKey = userProfile?.role?.key || '';
  const isBiologicAdmin = roleKey === RoleKey.BIOLOGIC_ADMIN;
  const institutionId = userProfile?.institution?.id || '';

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.TRAINER_FULL_TIME]);
  const navigateTo = useNavigate();
  const [trainers, setTrainers] = useState<Trainer[]>([]);

  const total = useNumber(0);
  const trainerIdSelected = useString('');

  const searchValue = useString('');

  const institutionState = useFetchApiToState<any, any>({
    url: '/institutions',
    data: {
      page: 1,
      limit: 9999,
    },
    method: 'GET',
  });

  const institutions = [
    { value: '', label: 'All institutions' },
    ...(institutionState.state?.items?.map((elInstitutions: Institution) => ({
      value: elInstitutions.id,
      label: elInstitutions.name,
    })) || []),
  ];

  const institutionValue = useString(institutionId);

  const searchSelect = useString('');

  useEffect(() => {
    if (!roleKey) {
      return;
    }

    if (roleKey === RoleKey.BIOLOGIC_ADMIN) {
      institutionState.fetchApi();
    }
  }, [roleKey]);

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.TRAINER_FULL_TIME, initialQueryState);

    return () => {
      updateQueryStateTable(TABLE_KEY.TRAINER_FULL_TIME, {
        ...initialQueryState,
        search: '',
      });
    };
  }, []);

  useEffect(() => {
    if (isEmpty(userProfile)) {
      return;
    }

    if (queryState) {
      getList();
    }
  }, [JSON.stringify(queryState), userProfile, institutionValue.value]);

  const getList = async () => {
    const result: {
      items: Trainer[];
      total: number;
    } = await apiCall({
      url: '/users',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...deleteDefaultQueryStateFromTable(queryState),
        roleKey: RoleKey.TRAINER_INSTITUTION,
        institutionId: institutionValue.value,
      },
    });

    setTrainers(result?.items ?? []);

    if (!isUndefined(result?.total)) {
      total.setValue(result.total);
    }
  };

  const onClickRow = (row: any) => {
    const original: Trainer = row?.original;

    if (original?.id) {
      redirectToDetail(original.id);
    }
  };

  const onClickAction = async (key: 'edit' | 'delete' | 'update-status', trainerId: string) => {
    switch (key) {
      case 'edit':
        redirectToDetail(trainerId);
        break;

      case 'delete':
        trainerIdSelected.setValue(trainerId);
        openModal(MODAL_KEY.DELETE_TRAINER);
        break;

      case 'update-status':
        await apiCall({
          url: `/users/${trainerId}/status`,
          method: 'PATCH',
          isLoading: true,
          showError: true,
        });

        showToastMessage('success', `Updated trainer's status successfully!`);

        getList();
        break;

      default:
        break;
    }
  };

  const redirectToDetail = (id: string) => {
    navigateTo(`${PATH.TRAINERS}/${PATH.TRAINER_FULL_TIME_DETAIL.replace(':id', id)}`);
  };

  const onSubmitDeleteTrainer = async () => {
    if (trainerIdSelected.value) {
      await apiCall({
        url: `/users/${trainerIdSelected.value}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });
      closeModal(MODAL_KEY.DELETE_TRAINER);
      showToastMessage('success', 'Removed Trainer successfully!');

      if (queryState.page === 1) {
        getList();

        return;
      }

      updateQueryStateTable(TABLE_KEY.TRAINER_FULL_TIME, {
        ...queryState,
        page: 1,
      });
    }
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.TRAINER_FULL_TIME, {
        ...queryState,
        page: 1,
        search: trimValue(newSearch),
      });
    }, 500),
    [],
  );

  const onChangeInstitution = (item: any) => {
    if (!item) {
      return;
    }

    institutionValue.setValue(item.value);

    updateQueryStateTable(TABLE_KEY.TRAINER_FULL_TIME, { ...queryState, page: 1 });
  };

  const onChangeSearchInstitution = (item: string) => {
    searchSelect.setValue(item);
  };

  return {
    isBiologicAdmin,
    trainers,
    total,
    queryState,
    searchValue,
    onClickRow,
    onClickAction,
    onSubmitDeleteTrainer,
    onChangeSearchValue,
    institutions,
    onChangeInstitution,
    institutionValue,
    onChangeSearchInstitution,
    searchSelect,
  };
};
